import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useRoute } from "wouter";

import { api, type UserStructuredProps } from "../lib/api";
import { useToast } from "../components/toast/ToastProvider";
import { LiveLog } from "../components/LiveLog";
import { EditConnectorModal } from "../components/plc/EditConnectorModal";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { cn } from "../lib/cn";
import { useWS } from "../lib/ws";

interface Source {
  id: string;
  name: string;
  connectorId: string;
  config: Record<string, unknown>;
  structuredProperties?: UserStructuredProps;
  scheduleCron: string;
  enabled: boolean;
  includeRawAttachments: boolean;
  lastRunId?: string;
  lastStatus?: string;
  createdAt: string;
}

interface CrawlRun {
  id: string;
  sourceId: string;
  startedAt: string;
  finishedAt?: string;
  status: "running" | "succeeded" | "failed" | "cancelled";
  entryCount: number;
  errorText?: string;
  logPath?: string;
}

export function SourceDetailPage() {
  const [, params] = useRoute("/sources/:id");
  const [, setLocation] = useLocation();
  const id = params?.id ?? "";

  const { show } = useToast();
  const qc = useQueryClient();
  const source = useQuery({
    queryKey: ["sources", id],
    queryFn: () => api.get<Source>(`/api/v1/sources/${encodeURIComponent(id)}`),
    enabled: !!id,
  });
  const runs = useQuery({
    queryKey: ["sources", id, "runs"],
    queryFn: () => api.get<CrawlRun[]>(`/api/v1/sources/${encodeURIComponent(id)}/runs`),
    enabled: !!id,
    refetchInterval: 5_000,
  });

  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Selected run for the log viewer. null = show live stream only (no stored log).
  // Set to a run ID only when the user explicitly clicks a row in run history.
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const selectedRunLog = useQuery({
    queryKey: ["runlog", selectedRunId],
    queryFn: async () => {
      const res = await fetch(`/api/v1/runs/${encodeURIComponent(selectedRunId!)}/log`, {
        credentials: "same-origin",
      });
      if (!res.ok) return "";
      return await res.text();
    },
    enabled: !!selectedRunId,
  });

  const refresh = useMutation({
    mutationFn: () =>
      api.post<{ runId: string }>(`/api/v1/sources/${encodeURIComponent(id)}/refresh`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sources", id, "runs"] });
      setSelectedRunId(null);
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : "Refresh failed to start";
      show("Refresh failed", msg, "error");
    },
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/api/v1/sources/${encodeURIComponent(id)}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sources"] });
      setLocation("/sources");
    },
  });

  const isRunning = runs.data?.[0]?.status === "running";

  // Cancel a specific run by ID. A source can have several runs in flight at
  // once (repeated refreshes, or a scheduled sync overlapping a manual one), so
  // cancellation is per-run rather than a single control for the source.
  const cancel = useMutation({
    mutationFn: (runId: string) => api.post(`/api/v1/runs/${encodeURIComponent(runId)}/cancel`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sources", id, "runs"] });
    },
  });

  useWS({
    sourceId: id,
    onEvent: (ev) => {
      if (ev.kind !== "crawl.status") return;
      qc.invalidateQueries({ queryKey: ["sources", id] });
      qc.invalidateQueries({ queryKey: ["sources", id, "runs"] });
      // Keep the sources list in sync for when the user navigates back.
      qc.invalidateQueries({ queryKey: ["sources"] });
    },
  });

  if (!id) return null;

  return (
    <div className="mx-auto w-full max-w-[1200px] px-10 pb-16">
      {/* HEADER */}
      <header className="pt-12">
        <button
          onClick={() => setLocation("/sources")}
          className="text-[12px] text-ash hover:text-ink"
        >
          ← All sources
        </button>
        <div className="mt-3 flex items-end justify-between gap-6">
          <div>
            <div className="eyebrow">Source</div>
            <h1 className="mt-2 font-display text-[40px] font-semibold leading-tight tracking-tightest text-ink">
              {source.data?.name ?? "—"}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="font-mono text-[11.5px] text-ash">{source.data?.connectorId}</span>
              <StatusBadge status={source.data?.lastStatus} />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setEditing(true)}
              disabled={!source.data}
              className="btn-secondary"
            >
              Edit
            </button>
            <button
              onClick={() => setLocation(`/sources/${encodeURIComponent(id)}/preview`)}
              disabled={!source.data}
              className="btn-secondary"
            >
              Preview
            </button>
            <button
              onClick={() => refresh.mutate()}
              disabled={refresh.isPending || isRunning}
              className="btn-primary"
            >
              {refresh.isPending ? "Enqueuing…" : isRunning ? "Running…" : "Refresh now →"}
            </button>
            <button onClick={() => setConfirmDelete(true)} className="btn-danger">
              Delete
            </button>
          </div>
        </div>
      </header>

      {/* LIVE LOG — pass initialText only when user has selected a specific run;
          undefined means LiveLog won't replace its lines when the value changes. */}
      <div className="mt-8">
        <LiveLog
          sourceId={id}
          initialText={selectedRunId ? (selectedRunLog.data ?? "") : undefined}
        />
      </div>

      {/* RUN HISTORY */}
      <section className="mt-6 card overflow-hidden">
        <div className="flex items-baseline justify-between border-b border-rule px-6 py-4">
          <h2 className="font-display text-[18px] font-semibold tracking-tight text-ink">
            Run history
          </h2>
          <span className="text-[11.5px] text-ash">{runs.data?.length ?? 0} recent runs</span>
        </div>
        {runs.isLoading ? <p className="px-6 py-8 text-sm text-ash">Loading…</p> : null}
        {runs.data && runs.data.length === 0 ? (
          <p className="px-6 py-8 text-sm text-ash">
            No runs yet. Click <strong>Refresh now</strong> to crawl this source.
          </p>
        ) : null}
        {runs.data && runs.data.length > 0 ? (
          <table className="w-full text-sm">
            <thead className="border-b border-rule bg-paper text-left">
              <tr>
                <Th>Started</Th>
                <Th>Duration</Th>
                <Th>Status</Th>
                <Th>Entries</Th>
                <Th>Error</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-rule">
              {runs.data.map((run) => {
                const isSelected = run.id === selectedRunId;
                return (
                  <tr
                    key={run.id}
                    onClick={() => setSelectedRunId(run.id)}
                    className={cn(
                      "cursor-pointer transition-colors",
                      isSelected ? "bg-brand-50/60" : "hover:bg-paper",
                    )}
                  >
                    <Td>
                      <div className="text-ink">{new Date(run.startedAt).toLocaleString()}</div>
                      <div className="font-mono text-[10.5px] text-ash">{run.id.slice(0, 8)}…</div>
                    </Td>
                    <Td>{durationOf(run)}</Td>
                    <Td>
                      <StatusBadge status={run.status} />
                    </Td>
                    <Td>
                      <span className="font-mono" data-num>
                        {run.entryCount}
                      </span>
                    </Td>
                    <Td>
                      {run.errorText ? (
                        <span
                          className="block max-w-[260px] truncate text-[11.5px] text-rose-700"
                          title={run.errorText}
                        >
                          {run.errorText}
                        </span>
                      ) : (
                        <span className="text-ash">—</span>
                      )}
                    </Td>
                    <Td>
                      {run.status === "running" ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            cancel.mutate(run.id);
                          }}
                          disabled={cancel.isPending && cancel.variables === run.id}
                          className="rounded-md border border-rose-200 px-2.5 py-1 text-[11px] font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                        >
                          {cancel.isPending && cancel.variables === run.id
                            ? "Cancelling…"
                            : "Cancel"}
                        </button>
                      ) : isSelected ? (
                        <span className="chip-brand">Viewing</span>
                      ) : (
                        <span className="text-[11px] text-ash">View log →</span>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : null}
      </section>

      {source.data && (
        <EditConnectorModal source={source.data} open={editing} onOpenChange={setEditing} />
      )}

      <ConfirmDialog
        open={confirmDelete}
        title="Delete source"
        description={`Delete "${source.data?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={() => {
          remove.mutate();
          setConfirmDelete(false);
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}

function StatusBadge({ status }: { status?: string }) {
  if (!status) return <span className="chip-neutral">Never run</span>;
  if (status === "succeeded")
    return (
      <span className="chip-emerald">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Succeeded
      </span>
    );
  if (status === "failed") return <span className="chip-rose">Failed</span>;
  if (status === "running")
    return (
      <span className="chip-amber animate-pulseSoft">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Running
      </span>
    );
  if (status === "cancelled") return <span className="chip-neutral">Cancelled</span>;
  return <span className="chip-neutral">{status}</span>;
}

function durationOf(run: CrawlRun): string {
  if (!run.finishedAt) return "—";
  const ms = new Date(run.finishedAt).getTime() - new Date(run.startedAt).getTime();
  if (ms < 1_000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1_000).toFixed(1)} s`;
  return `${Math.round(ms / 60_000)} min`;
}

function Th({ children }: { children?: React.ReactNode }) {
  return (
    <th className="px-5 py-3 font-mono text-[10.5px] font-semibold uppercase tracking-[0.18em] text-ash">
      {children}
    </th>
  );
}

function Td({ children }: { children?: React.ReactNode }) {
  return <td className="px-5 py-4 align-top">{children}</td>;
}
